import { isGroupRow } from '../../../../stores/utils/group-row-guards';
import { parseSupervisorRouting } from '../../../../pages/canvas-view/canvas-edges-model';
// TYPE-ONLY: the runtime store drags the Lit design system into node-env
// unit tests (same reason `canvas-edges-model.ts` imports it this way).
import type { TableNode } from '../../../../stores/table-store';

/**
 * AI Action Assist's pure logic: what the model is shown about the solution,
 * which variables the solution already has, and how a reviewed draft maps
 * onto the node's real columns.
 */

export type DraftColumn =
  | 'nodeName'
  | 'command'
  | 'description'
  | 'nodeInput'
  | 'parameterInput'
  | 'output'
  | 'decisionVariable'
  | 'whatsNext'
  | 'variables';

export interface DraftField {
  column: DraftColumn;
  value: string;
  why: string;
  changed?: boolean;
}

/** Card labels, in the order the prototype's review shows them. */
export const DRAFT_COLUMN_LABELS: Record<DraftColumn, string> = {
  nodeName: 'Node Name',
  command: 'Command',
  description: 'Description',
  nodeInput: 'Node Input',
  parameterInput: 'Parameter Input',
  output: 'Output',
  decisionVariable: 'Decision Variable',
  whatsNext: 'What Next?',
  variables: 'Variable',
};

export const DRAFT_COLUMN_ORDER = Object.keys(
  DRAFT_COLUMN_LABELS
) as DraftColumn[];

/** How a review card renders its value. */
export const draftColumnKind = (
  column: DraftColumn
): 'text' | 'pills' | 'json' | 'map' => {
  if (column === 'parameterInput') return 'json';
  if (column === 'whatsNext') return 'map';
  if (
    column === 'nodeInput' ||
    column === 'output' ||
    column === 'decisionVariable' ||
    column === 'variables'
  ) {
    return 'pills';
  }
  return 'text';
};

/** Variables on this platform are UPPERCASE_WITH_UNDERSCORES; normalise
 *  rather than reject, as the prototype's picker does. */
export const normVar = (s: string): string =>
  String(s || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^A-Z0-9_]/g, '');

export const splitList = (s: string): string[] =>
  String(s || '')
    .split(/[,;|]/)
    .map((v) => v.trim())
    .filter(Boolean);

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

const asText = (v: unknown): string => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') {
    const d = (v as { default?: unknown }).default;
    if (typeof d === 'string') return d;
    try {
      return JSON.stringify(v);
    } catch {
      return '';
    }
  }
  return String(v);
};

/** A column's current value as the panel's text editors show it. */
export const currentFieldText = (
  row: TableNode,
  column: DraftColumn
): string => {
  const value = (row as unknown as Record<string, unknown>)[column];
  if (column === 'whatsNext') return asArray(value).map(String).join('|');
  if (column === 'variables' || column === 'output') {
    return asArray(value).map(String).filter(Boolean).join(',');
  }
  if (column === 'parameterInput') {
    if (value == null || value === '') return '';
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  }
  return asText(value);
};

const VAR_TOKEN = /\{([A-Z][A-Z0-9_]*)\}/g;

/**
 * Every variable the solution already names: each node's Variable column,
 * Node Input, Output and Decision Variable, plus `{NAME}` interpolations in
 * messages and Parameter Input. So a drafted reference points at a name that
 * exists.
 */
export const knownVariables = (rows: TableNode[]): string[] => {
  const seen = new Set<string>();
  const add = (name: string) => {
    const n = normVar(name);
    if (n) seen.add(n);
  };
  rows.forEach((row) => {
    if (isGroupRow(row)) return;
    asArray(row.variables).forEach((v) => add(String(v)));
    splitList(asText(row.nodeInput)).forEach(add);
    asArray(row.output).forEach((v) => add(String(v)));
    add(asText(row.decisionVariable));
    const text = `${asText(row.message)} ${asText(row.parameterInput)}`;
    for (const m of text.matchAll(VAR_TOKEN)) add(m[1]);
  });
  return [...seen].sort((a, b) => a.localeCompare(b));
};

const clip = (s: string, max: number): string => {
  const t = s.replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

const quote = (s: string) => `"${s.replace(/"/g, "'")}"`;

/**
 * A row's buttons as `label~node` pairs (`richAssetContent`, `|`-separated).
 * The engine routes a press to that node, so a node with buttons waits for
 * one instead of ending the chat.
 */
const buttonsOf = (row: TableNode): { label: string; to: string }[] =>
  String(row.richAssetType ?? '').toLowerCase() === 'button'
    ? asText(row.richAssetContent)
        .split('|')
        .map((pair) => {
          const [label, to] = pair.split('~');
          return { label: (label ?? '').trim(), to: (to ?? '').trim() };
        })
        .filter((b) => b.label && b.to)
    : [];

/** Node numbers this row routes to, from every place routing lives. */
const destinationsOf = (row: TableNode): string[] => {
  const out = new Set<string>();
  asArray(row.nextNodes).forEach((n) => n != null && out.add(String(n)));
  asArray(row.whatsNext).forEach((pair) => {
    const dest = String(pair).split('~')[1];
    if (dest) out.add(dest.trim());
  });
  parseSupervisorRouting(row).forEach((b) => out.add(b.destination));
  buttonsOf(row).forEach((b) => out.add(b.to));
  return [...out];
};

const nodeLine = (
  row: TableNode,
  groupNames: Map<string, string>,
  isCurrent: boolean
): string => {
  const parts = [
    `#${row.nodeNumber ?? '?'}`,
    `[${String(row.nodeType ?? '').toUpperCase()}]`,
    quote(clip(asText(row.nodeName), 90)),
  ];
  const groupId = row.settings?.groupId;
  const group = groupId ? groupNames.get(String(groupId)) : undefined;
  if (group) parts.push(`group=${quote(clip(group, 50))}`);
  const field = (label: string, value: string, max: number) => {
    if (value.trim()) parts.push(`${label}=${clip(value, max)}`);
  };
  field('command', asText(row.command), 60);
  field(
    'next',
    asArray(row.nextNodes)
      .filter((n) => n != null)
      .join(','),
    80
  );
  field('whatsNext', asArray(row.whatsNext).map(String).join('|'), 200);
  field(
    'buttons',
    buttonsOf(row)
      .map((b) => `${b.label}~${b.to}`)
      .join('|'),
    200
  );
  // A node that waits for the customer doesn't end the chat, and a hand-off
  // is a behavior, not a next node: without these, every hand-off step reads
  // as a message that leads nowhere.
  if (row.answerRequired === true) parts.push('waits=true');
  field(
    'behaviors',
    asArray(row.behaviors).map(String).filter(Boolean).join(','),
    120
  );
  field('dv', asText(row.decisionVariable), 60);
  field('in', asText(row.nodeInput), 120);
  field('out', asArray(row.output).map(String).join(','), 120);
  field(
    'vars',
    asArray(row.variables).map(String).filter(Boolean).join(','),
    160
  );
  const routes = parseSupervisorRouting(row);
  if (routes.length) {
    field(
      'routes',
      `{${routes.map((b) => `${b.label}:${b.destination}`).join(', ')}}`,
      400
    );
  } else {
    field('params', asText(row.parameterInput), 200);
  }
  const utter = asText(row.utterances);
  if (utter.trim()) parts.push(`utter=${quote(clip(utter, 120))}`);
  const msg = asText(row.message);
  if (msg.trim()) parts.push(`msg=${quote(clip(msg, 140))}`);
  if (isCurrent) parts.push('(CURRENT)');
  return parts.join(' ');
};

/** Well inside the server's 60k cap, and a few thousand tokens at most. */
export const DEFAULT_CONTEXT_CHARS = 24_000;

/**
 * The solution as text for the model: one line per node, in solution order.
 *
 * When it would pass `maxChars` (a 500-node IVR does), the current node and
 * every node it routes to or is routed from are kept first — those are what
 * questions about "this node" need — then the rest in order until the cap,
 * with a closing line saying how many were left out, so the model knows the
 * list is partial rather than concluding a node does not exist.
 */
export const summarizeSolution = (
  rows: TableNode[],
  currentRowId: string | null,
  { maxChars = DEFAULT_CONTEXT_CHARS }: { maxChars?: number } = {}
): string => {
  const groupNames = new Map<string, string>();
  rows.forEach((row) => {
    if (isGroupRow(row)) {
      groupNames.set(row.id, asText(row.settings?.groupName));
    }
  });

  const nodes = rows.filter((row) => !isGroupRow(row));
  const lines = nodes.map((row) =>
    nodeLine(row, groupNames, row.id === currentRowId)
  );
  const total = lines.reduce((n, l) => n + l.length + 1, 0);
  if (total <= maxChars) return lines.join('\n');

  const current = nodes.find((row) => row.id === currentRowId);
  const priority = new Set<number>();
  if (current) {
    const currentNum = String(current.nodeNumber ?? '');
    const outgoing = new Set(destinationsOf(current));
    nodes.forEach((row, i) => {
      const num = String(row.nodeNumber ?? '');
      if (
        row.id === currentRowId ||
        outgoing.has(num) ||
        destinationsOf(row).includes(currentNum)
      ) {
        priority.add(i);
      }
    });
  }

  const keep = new Set<number>();
  let used = 0;
  const tryAdd = (i: number) => {
    const cost = lines[i].length + 1;
    if (used + cost > maxChars) return false;
    keep.add(i);
    used += cost;
    return true;
  };
  priority.forEach((i) => tryAdd(i));
  for (let i = 0; i < lines.length; i += 1) {
    if (!keep.has(i) && !tryAdd(i)) break;
  }

  const shown = lines.filter((_, i) => keep.has(i));
  const omitted = lines.length - shown.length;
  return `${shown.join('\n')}\n(${omitted} more node${
    omitted === 1 ? '' : 's'
  } not shown — the list is partial, so do not conclude a node is missing.)`;
};

/**
 * The value each kept field commits, as the text the panel's own editors
 * would type. Variable lists are normalised so the Variable column's
 * validator doesn't silently drop the whole list over one lowercase name.
 */
export const draftFieldCommitText = (field: DraftField): string => {
  if (field.column === 'variables' || field.column === 'nodeInput') {
    return splitList(field.value).map(normVar).filter(Boolean).join(',');
  }
  if (field.column === 'decisionVariable') return normVar(field.value);
  if (field.column === 'parameterInput') {
    try {
      return JSON.stringify(JSON.parse(field.value), null, 2);
    } catch {
      return field.value;
    }
  }
  return field.value.trim();
};

/** Review-card tag: a new value, one replacing what the node had, or one a
 *  refine just changed. */
export const draftFieldTag = (
  row: TableNode,
  field: DraftField
): 'new' | 'overwrite' | 'updated' => {
  if (field.changed) return 'updated';
  return currentFieldText(row, field.column).trim() ? 'overwrite' : 'new';
};

/** Orders fields as the review shows them and drops any column outside the
 *  draftable set. */
export const orderDraftFields = (fields: DraftField[]): DraftField[] =>
  DRAFT_COLUMN_ORDER.flatMap((column) =>
    fields.filter((f) => f.column === column).slice(0, 1)
  );
